import { AuthorizationService, HttpService } from '@bentley/pw-api';
import { loadServerInfoFromStorage, saveServerInfoToStorage } from './storage';
/**
 * Sends a dummy request to the datasource connection to retrieve server
 * version information for WSG, web api and PW plugin.
 * @param datasourceUrl ProjectWise datasource URL
 * @param authorization Configured AuthorizationService for the datasourceUrl OR function to get oidc token and/or function to get saml token
 * @returns PluginInfo object with version information
 */
export async function getServerInfo(datasourceUrl, authorization) {
    const cachedServerInfo = loadServerInfoFromStorage(datasourceUrl);
    if (cachedServerInfo) {
        void fetchAndStoreServerInfo(datasourceUrl, authorization);
        return cachedServerInfo;
    }
    const serverInfo = await fetchAndStoreServerInfo(datasourceUrl, authorization);
    return serverInfo;
}
async function fetchAndStoreServerInfo(datasourceUrl, authorization) {
    try {
        const serverResponse = await contactConnectionEndpoint(datasourceUrl, authorization);
        const pwPluginVersion = parseServerHeader(serverResponse, 'Bentley.PW');
        const webApiVersion = parseServerHeader(serverResponse, 'Bentley-WebAPI');
        const wsgVersion = parseServerHeader(serverResponse, 'Bentley-WSG');
        const serverInfo = { pwPluginVersion, webApiVersion, wsgVersion };
        saveServerInfoToStorage(datasourceUrl, serverInfo);
        return serverInfo;
    }
    catch (error) {
        console.error('Error fetching server info:', error);
        return { pwPluginVersion: '', webApiVersion: '', wsgVersion: '' };
    }
}
async function contactConnectionEndpoint(datasourceUrl, authorization) {
    const authorizationService = await configureAuthorizationService(datasourceUrl, authorization);
    const httpService = new HttpService({
        authorization: authorizationService,
        baseUrl: datasourceUrl
    });
    const versionEndpoint = datasourceUrl.toLowerCase().includes('repositories')
        ? 'PW_WSG/CheckConnectionHeaders'
        : 'v2.0/Plugins';
    const response = await httpService.get(versionEndpoint);
    return response;
}
async function configureAuthorizationService(datasourceUrl, authorization) {
    if (authorization instanceof AuthorizationService) {
        return authorization;
    }
    if (!authorization.getOidcToken && !authorization.getSamlToken) {
        throw new Error('Authorization must include either getOidcToken or getSamlToken');
    }
    const authorizationService = await AuthorizationService.Create({
        baseUrl: datasourceUrl,
        getOidcToken: authorization.getOidcToken,
        getSamlToken: authorization.getSamlToken
    });
    if (!authorizationService) {
        throw new Error('Failed to configure authorization service with provided tokens');
    }
    return authorizationService;
}
function parseServerHeader(response, serverType) {
    const serverHeaders = getServerHeaders(response);
    const headerValue = getServerVersion(serverHeaders, serverType);
    return headerValue;
}
function getServerHeaders(response) {
    var _a;
    const serverHeader = (_a = response.headers.get('mas-server')) !== null && _a !== void 0 ? _a : response.headers.get('server');
    if (!serverHeader) {
        return [];
    }
    return serverHeader.split(',');
}
function getServerVersion(serverHeaders, serverType) {
    const serverHeader = serverHeaders.find((header) => header.trim().toLowerCase().startsWith(serverType.toLowerCase()));
    if (!serverHeader) {
        return '';
    }
    const versionString = serverHeader.split('/')[1];
    return versionString;
}
